import * as React from 'react'
import * as PropTypes from 'prop-types'
import moment from 'moment'
import { css } from 'glamor'
import { FormattedMessage, defineMessages } from 'react-intl'

import { withTheme, IWithThemeProps, useTheme } from '../../theme'

import { Touchable } from '../touchable'
import { IDatepickerPredefinedRange } from './types'
import { AnimateChildHeight } from '../animate-child-height'
import { ExpandButton } from '../expand-button'
import { Flex } from '../flex'

export const predefinedRangeShape = PropTypes.shape({
  from: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  to: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
})

const messages = defineMessages({
  thisMonth: {
    id: 'ui-kit.daterange-dropdown.range-this-month',
    defaultMessage: 'This month',
  },
  lastThreeMonths: {
    id: 'ui-kit.daterange-dropdown.range-last-three-months',
    defaultMessage: 'Last three months',
  },
  lastSixMonths: {
    id: 'ui-kit.daterange-dropdown.range-last-six-months',
    defaultMessage: 'Last six months',
  },
})
const ranges: IDatepickerPredefinedRange[] = [
  {
    message: <FormattedMessage {...messages.thisMonth} />,
    from: moment().startOf('month'),
    to: moment(),
  },
  {
    message: <FormattedMessage {...messages.lastThreeMonths} />,
    from: moment().subtract(3, 'months'),
    to: moment(),
  },
  {
    message: <FormattedMessage {...messages.lastSixMonths} />,
    from: moment().subtract(6, 'months'),
    to: moment(),
  },
]

/* eslint-disable react/prop-types */
const DaterangeDropdownRangesItem: React.FC<{
  onClick: (range: { fromDate: moment.Moment; toDate: moment.Moment }) => void
  range: IDatepickerPredefinedRange
}> = props => {
  const theme = useTheme()
  const [isOpen, setOpen] = React.useState(false)

  const itemStyle = {
    width: '100%',
    boxSizing: 'border-box',
    paddingBottom: theme.settings.spacing.small,
    paddingTop: theme.settings.spacing.small,
    paddingLeft: theme.settings.spacing.large,
    paddingRight: theme.settings.spacing.large,
    borderBottom: theme.settings.borders.dim,
    cursor: 'pointer',
    fontWeight: theme.settings.typography.weights.semibold,
    ':hover': {
      backgroundColor: theme.colors.backgrounds.mainHover,
    },
    ':focus': {
      outline: 0,
      backgroundColor: theme.colors.backgrounds.mainHover,
    },
  }

  return (
    <li>
      <Touchable
        onClick={() => props.onClick({ toDate: moment(props.range.to), fromDate: moment(props.range.from) })}
        style={itemStyle}
      >
        <Flex alignItems="center" justifyContent="space-between" row>
          {props.range.message}

          {props.range.subranges && props.range.subranges.length > 0 && (
            <ExpandButton expanded={isOpen} flush onClick={() => setOpen(value => !value)} />
          )}
        </Flex>
      </Touchable>

      {props.range.subranges && props.range.subranges.length > 0 && (
        <AnimateChildHeight>
          {isOpen && (
            <ul
              {...css({
                width: '100%',
                listStyleType: 'none',
                padding: 0,
                margin: 0,
                color: theme.colors.text.normal,
              })}
            >
              {props.range.subranges.map(subrange => (
                <Touchable
                  component="li"
                  key={moment(subrange.from).format('YYYY-MM-DD') + moment(subrange.to).format('YYYY-MM-DD')}
                  onClick={() => props.onClick({ toDate: moment(subrange.to), fromDate: moment(subrange.from) })}
                  style={[
                    itemStyle,
                    {
                      paddingLeft: theme.settings.spacing.xLarge,
                      fontWeight: theme.settings.typography.weights.normal,
                    },
                  ]}
                >
                  {subrange.message}
                </Touchable>
              ))}
            </ul>
          )}
        </AnimateChildHeight>
      )}
    </li>
  )
}
/* eslint-enable react/prop-types */

interface IDaterangeDropdownRangesProps {
  onClick: (range: { fromDate: moment.Moment; toDate: moment.Moment }) => void
  predefinedRanges?: IDatepickerPredefinedRange[]
  flush?: boolean
}

export const DaterangeDropdownRanges = withTheme(
  class DaterangeDropdownRanges extends React.Component<IDaterangeDropdownRangesProps & IWithThemeProps> {
    static propTypes = {
      flush: PropTypes.bool,
      onClick: PropTypes.func.isRequired,
      predefinedRanges: PropTypes.arrayOf(predefinedRangeShape) as any,
    }

    render() {
      const { onClick, predefinedRanges, flush, theme } = this.props

      return (
        <ul
          {...css({
            width: '100%',
            marginBottom: !flush && theme.settings.spacing.small,
            listStyleType: 'none',
            padding: 0,
            marginTop: 0,
            color: theme.colors.text.normal,
            minWidth: '160px',
          })}
        >
          {(predefinedRanges || ranges).map(range => (
            <DaterangeDropdownRangesItem
              key={moment(range.from).format('YYYY-MM-DD') + moment(range.to).format('YYYY-MM-DD')}
              onClick={onClick}
              range={range}
            />
          ))}
        </ul>
      )
    }
  }
)
